import React, { useState, useMemo } from 'react';
import { 
  X, 
  Search, 
  Clock, 
  Users, 
  MapPin, 
  Calendar,
  Star,
  Trophy,
  Music,
  User,
  Building,
  Tag,
  Hash,
  ChevronDown,
  ChevronUp
} from 'lucide-react';
import { useTheme } from '../../context/ThemeContext';

const EnhancedRoutineViewer = ({ 
  routines, 
  stageName, 
  onClose,
  show,
  eventTimezone = null
}) => {
  const { theme } = useTheme();
  const isDarkMode = theme === 'dark';
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedRoutine, setExpandedRoutine] = useState(null);
  const [sortBy, setSortBy] = useState('routineIndex');
  const [sortDirection, setSortDirection] = useState('asc');

  // Format time for display using eventTimezone
  const formatTime = (timeString) => {
    if (!timeString) return 'Not scheduled';
    try {
      const date = new Date(timeString);
      const options = {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      };
      if (eventTimezone) {
        options.timeZone = eventTimezone;
      }
      return date.toLocaleTimeString('en-US', options);
    } catch (error) {
      return 'Invalid time';
    }
  };

  // Format date for display using eventTimezone
  const formatDate = (timeString) => {
    if (!timeString) return 'Not scheduled';
    try {
      const date = new Date(timeString);
      const options = {
        month: 'short',
        day: 'numeric'
      };
      if (eventTimezone) {
        options.timeZone = eventTimezone;
      }
      return date.toLocaleDateString('en-US', options);
    } catch (error) {
      return 'Invalid date';
    }
  };

  // Get performer name from the routine data
  // Helper function to get submission routine title (prefers lastSubmitted, then first)
  const getSubmissionRoutineTitle = (routine) => {
    if (!routine?.parentRoutine?.submissionRoutines || 
        !Array.isArray(routine.parentRoutine.submissionRoutines) || 
        routine.parentRoutine.submissionRoutines.length === 0) {
      // Fallback to parentRoutine.title if no submission routines exist
      return routine?.parentRoutine?.title;
    }
    
    // Prefer the routine with lastSubmitted: true
    const lastSubmitted = routine.parentRoutine.submissionRoutines.find(sr => sr.lastSubmitted);
    const submissionRoutine = lastSubmitted || routine.parentRoutine.submissionRoutines[0];
    
    return submissionRoutine?.title || routine?.parentRoutine?.title;
  };

  const getPerformerName = (routine) => {
    // Try to get from parent routine first
    if (routine.parentRoutine?.routineDancers?.length > 0) {
      const dancers = routine.parentRoutine.routineDancers;
      if (dancers.length === 1) {
        const dancer = dancers[0].rosterDancers;
        return `${dancer.firstName} ${dancer.lastName}`;
      } else {
        // For multiple dancers, just return the count (no "Unknown dancer")
        return `${dancers.length} Dancers`;
      }
    }
    return 'Unknown Performer';
  };

  // Get dancer count from the routine data
  const getDancerCount = (routine) => {
    return routine.parentRoutine?.routineDancers?.length || 0;
  };

  // Get additional routine info (ageUp, extraTime, routineType)
  const getRoutineInfo = (routine) => {
    return {
      ageUp: routine.parentRoutine?.ageUp || false,
      extraTime: routine.parentRoutine?.extraTime || '0',
      routineType: routine.parentRoutine?.routineType || 'normal'
    };
  };

  // Get studio name from the routine data
  const getStudioName = (routine) => {
    try {
      return routine.parentRoutine?.submissionRoutines?.[0]?.registrations?.studios?.businessName || 'Unknown Studio';
    } catch (error) {
      return 'Unknown Studio';
    }
  };

  // Get category information
  const getCategoryInfo = (routine) => {
    try {
      const submission = routine.parentRoutine?.submissionRoutines?.[0];
      return {
        category: submission?.category?.name || 'Unknown',
        division: submission?.division?.name || 'Unknown',
        level: submission?.level?.name || 'Unknown'
      };
    } catch (error) {
      return {
        category: 'Unknown',
        division: 'Unknown', 
        level: 'Unknown'
      };
    }
  };

  // Filter and sort routines
  const filteredAndSortedRoutines = useMemo(() => {
    // First, deduplicate routines by ID and routineIndex
    const uniqueRoutines = routines.reduce((acc, routine) => {
      const key = `${routine.id}-${routine.routineIndex}`;
      if (!acc.has(key)) {
        acc.set(key, routine);
      }
      return acc;
    }, new Map());
    
    let filtered = Array.from(uniqueRoutines.values()).filter(routine => {
      if (!searchQuery) return true;
      const query = searchQuery.toLowerCase();
      const performerName = getPerformerName(routine).toLowerCase();
      const studioName = getStudioName(routine).toLowerCase();
      const categoryInfo = getCategoryInfo(routine);
      const routineTitle = getSubmissionRoutineTitle(routine)?.toLowerCase() || '';
      const routineInfo = getRoutineInfo(routine);
      
      return (
        routine.number?.toString().includes(query) ||
        routine.routineIndex?.toString().includes(query) ||
        performerName.includes(query) ||
        studioName.includes(query) ||
        routineTitle.includes(query) ||
        categoryInfo.category.toLowerCase().includes(query) ||
        categoryInfo.division.toLowerCase().includes(query) ||
        categoryInfo.level.toLowerCase().includes(query) ||
        routineInfo.routineType.toLowerCase().includes(query) ||
        (routineInfo.ageUp && 'age up'.includes(query)) ||
        (routineInfo.extraTime !== '0' && `extra time ${routineInfo.extraTime}`.includes(query))
      );
    });

    // Sort the filtered results
    filtered.sort((a, b) => {
      let aVal, bVal;
      
      switch (sortBy) {
        case 'routineIndex':
          aVal = a.routineIndex || 0;
          bVal = b.routineIndex || 0;
          break;
        case 'time':
          aVal = new Date(a.startTime || 0);
          bVal = new Date(b.startTime || 0);
          break;
        case 'performer':
          aVal = getPerformerName(a);
          bVal = getPerformerName(b);
          break;
        case 'studio':
          aVal = getStudioName(a);
          bVal = getStudioName(b);
          break;
        default:
          aVal = a[sortBy] || '';
          bVal = b[sortBy] || '';
      }

      if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });

    return filtered;
  }, [routines, searchQuery, sortBy, sortDirection]);

  const handleSort = (field) => {
    if (sortBy === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortDirection('asc');
    }
  };

  if (!show) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div 
        className="fixed inset-0 bg-black/60 backdrop-blur-sm" 
        onClick={onClose}
        aria-hidden="true"
      />
      <div className="fixed inset-0 flex items-center justify-center p-4">
        <div 
          className={`relative w-full max-w-7xl rounded-lg shadow-xl ${
            isDarkMode 
              ? 'bg-[#1a1f2d]' 
              : 'bg-white'
          }`}
          onClick={e => e.stopPropagation()}
        >
          {/* Header */}
          <div className={`flex items-center justify-between p-6 border-b ${
            isDarkMode ? 'border-gray-600' : 'border-gray-200'
          }`}>
            <div>
              <h3 className={`text-xl font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                Enhanced Schedule Preview
              </h3>
              <p className={`text-sm mt-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                {stageName} • {filteredAndSortedRoutines.length} of {routines.length} routines
              </p>
            </div>
            <button 
              onClick={onClose} 
              className="btn btn-ghost btn-sm btn-circle"
              type="button"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Controls */}
          <div className="p-6 border-b border-gray-200 dark:border-gray-600">
            <div className="flex flex-col sm:flex-row gap-4">
              {/* Search */}
              <div className="flex-1">
                <div className="relative">
                  <input
                    type="text"
                    placeholder="Search routines, performers, studios..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className={`w-full px-4 py-2 pl-10 rounded-lg border ${
                      isDarkMode 
                        ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' 
                        : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
                    } focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
                  />
                  <Search className={`absolute left-3 top-2.5 h-4 w-4 ${
                    isDarkMode ? 'text-gray-400' : 'text-gray-500'
                  }`} />
                </div>
              </div>

              {/* Sort Controls */}
              <div className="flex gap-2">
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  className={`px-3 py-2 rounded-lg border ${
                    isDarkMode 
                      ? 'bg-gray-700 border-gray-600 text-white' 
                      : 'bg-white border-gray-300 text-gray-900'
                  }`}
                >
                  <option value="routineIndex">Order</option>
                  <option value="time">Time</option>
                  <option value="performer">Performer</option>
                  <option value="studio">Studio</option>
                </select>
                <button
                  onClick={() => setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc')}
                  className={`px-3 py-2 rounded-lg border ${
                    isDarkMode 
                      ? 'bg-gray-700 border-gray-600 text-white hover:bg-gray-600' 
                      : 'bg-white border-gray-300 text-gray-900 hover:bg-gray-50'
                  }`}
                >
                  {sortDirection === 'asc' ? '↑' : '↓'}
                </button>
              </div>
            </div>
          </div>

          {/* Content */}
          <div className="p-6 max-h-[calc(90vh-300px)] overflow-y-auto">
            <div className="space-y-4">
              {filteredAndSortedRoutines.map((routine, index) => {
                const performerName = getPerformerName(routine);
                const studioName = getStudioName(routine);
                const categoryInfo = getCategoryInfo(routine);
                const dancerCount = getDancerCount(routine);
                const routineInfo = getRoutineInfo(routine);
                const uniqueId = `${routine.id || 'routine'}-${routine.routineIndex || index}-${index}`;
                const isExpanded = expandedRoutine === uniqueId;

                return (
                  <div 
                    key={`${routine.id || 'routine'}-${routine.routineIndex || index}-${index}`}
                    className={`rounded-lg border ${
                      isDarkMode 
                        ? 'border-gray-600 bg-gray-800/50' 
                        : 'border-gray-200 bg-gray-50/50'
                    } overflow-hidden`}
                  >
                    {/* Main routine info */}
                    <div className="p-4">
                      <div className="flex items-start justify-between">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-3 mb-2">
                            <div className={`flex items-center justify-center w-8 h-8 rounded-full ${
                              isDarkMode ? 'bg-blue-600' : 'bg-blue-100'
                            }`}>
                              <span className={`text-sm font-semibold ${
                                isDarkMode ? 'text-white' : 'text-blue-800'
                              }`}>
                                {routine.number || routine.routineIndex}
                              </span>
                            </div>
                            <div>
                              <h4 className={`font-semibold ${
                                isDarkMode ? 'text-white' : 'text-gray-900'
                              }`}>
                                {getSubmissionRoutineTitle(routine) || 'Untitled Routine'}
                              </h4>
                              <p className={`text-sm ${
                                isDarkMode ? 'text-gray-400' : 'text-gray-600'
                              }`}>
                                {performerName}
                              </p>
                            </div>
                          </div>

                          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                            <div className="flex items-center gap-2">
                              <Building className={`h-4 w-4 ${
                                isDarkMode ? 'text-gray-400' : 'text-gray-500'
                              }`} />
                              <span className={isDarkMode ? 'text-gray-300' : 'text-gray-700'}>
                                {studioName}
                              </span>
                            </div>

                            <div className="flex items-center gap-2">
                              <Clock className={`h-4 w-4 ${
                                isDarkMode ? 'text-gray-400' : 'text-gray-500'
                              }`} />
                              <span className={isDarkMode ? 'text-gray-300' : 'text-gray-700'}>
                                {formatTime(routine.startTime)}
                              </span>
                            </div>

                            <div className="flex items-center gap-2">
                              <Tag className={`h-4 w-4 ${
                                isDarkMode ? 'text-gray-400' : 'text-gray-500'
                              }`} />
                              <span className={isDarkMode ? 'text-gray-300' : 'text-gray-700'}>
                                {categoryInfo.category}
                              </span>
                            </div>

                            <div className="flex items-center gap-2">
                              <Users className={`h-4 w-4 ${
                                isDarkMode ? 'text-gray-400' : 'text-gray-500'
                              }`} />
                              <span className={isDarkMode ? 'text-gray-300' : 'text-gray-700'}>
                                {dancerCount} {dancerCount === 1 ? 'Dancer' : 'Dancers'}
                              </span>
                            </div>

                            <div className="flex items-center gap-2">
                              <Tag className={`h-4 w-4 ${
                                isDarkMode ? 'text-gray-400' : 'text-gray-500'
                              }`} />
                              <span className={isDarkMode ? 'text-gray-300' : 'text-gray-700'}>
                                {categoryInfo.division}
                              </span>
                            </div>
                          </div>

                          {/* Additional badges for special attributes */}
                          <div className="flex items-center gap-2 flex-wrap mt-2">
                            {routineInfo.ageUp && (
                              <span className={`text-xs px-2 py-1 rounded-full ${
                                isDarkMode ? 'bg-orange-900 text-orange-200' : 'bg-orange-100 text-orange-800'
                              }`}>
                                Age Up
                              </span>
                            )}
                            {routineInfo.extraTime !== '0' && (
                              <span className={`text-xs px-2 py-1 rounded-full ${
                                isDarkMode ? 'bg-purple-900 text-purple-200' : 'bg-purple-100 text-purple-800'
                              }`}>
                                +{routineInfo.extraTime}s Extra
                              </span>
                            )}
                            {routineInfo.routineType !== 'normal' && (
                              <span className={`text-xs px-2 py-1 rounded-full ${
                                isDarkMode ? 'bg-red-900 text-red-200' : 'bg-red-100 text-red-800'
                              }`}>
                                {routineInfo.routineType.charAt(0).toUpperCase() + routineInfo.routineType.slice(1)}
                              </span>
                            )}
                          </div>
                        </div>

                        <button
                          onClick={() => setExpandedRoutine(isExpanded ? null : uniqueId)}
                          className={`ml-4 p-2 rounded-lg transition-colors ${
                            isDarkMode 
                              ? 'hover:bg-gray-700 text-gray-400' 
                              : 'hover:bg-gray-200 text-gray-600'
                          }`}
                        >
                          {isExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                        </button>
                      </div>
                    </div>

                    {/* Expanded details */}
                    {isExpanded && (
                      <div className={`border-t px-4 pb-4 ${
                        isDarkMode ? 'border-gray-600 bg-gray-800/30' : 'border-gray-200 bg-white/50'
                      }`}>
                        <div className="pt-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                          {/* Timing Details */}
                          <div>
                            <h5 className={`font-medium mb-2 ${
                              isDarkMode ? 'text-white' : 'text-gray-900'
                            }`}>
                              Timing
                            </h5>
                            <div className="space-y-1 text-sm">
                              <div className="flex justify-between">
                                <span className={isDarkMode ? 'text-gray-400' : 'text-gray-600'}>Start:</span>
                                <span className={isDarkMode ? 'text-gray-300' : 'text-gray-700'}>
                                  {formatTime(routine.startTime)}
                                </span>
                              </div>
                              <div className="flex justify-between">
                                <span className={isDarkMode ? 'text-gray-400' : 'text-gray-600'}>End:</span>
                                <span className={isDarkMode ? 'text-gray-300' : 'text-gray-700'}>
                                  {formatTime(routine.endTime)}
                                </span>
                              </div>
                              <div className="flex justify-between">
                                <span className={isDarkMode ? 'text-gray-400' : 'text-gray-600'}>Date:</span>
                                <span className={isDarkMode ? 'text-gray-300' : 'text-gray-700'}>
                                  {formatDate(routine.startTime)}
                                </span>
                              </div>
                            </div>
                          </div>

                          {/* Competition Details */}
                          <div>
                            <h5 className={`font-medium mb-2 ${
                              isDarkMode ? 'text-white' : 'text-gray-900'
                            }`}>
                              Competition Details
                            </h5>
                            <div className="space-y-1 text-sm">
                              <div className="flex justify-between">
                                <span className={isDarkMode ? 'text-gray-400' : 'text-gray-600'}>Level:</span>
                                <span className={isDarkMode ? 'text-gray-300' : 'text-gray-700'}>
                                  {categoryInfo.level}
                                </span>
                              </div>
                              <div className="flex justify-between">
                                <span className={isDarkMode ? 'text-gray-400' : 'text-gray-600'}>Division:</span>
                                <span className={isDarkMode ? 'text-gray-300' : 'text-gray-700'}>
                                  {categoryInfo.division}
                                </span>
                              </div>
                              <div className="flex justify-between">
                                <span className={isDarkMode ? 'text-gray-400' : 'text-gray-600'}>Dancers:</span>
                                <span className={isDarkMode ? 'text-gray-300' : 'text-gray-700'}>
                                  {dancerCount}
                                </span>
                              </div>
                              <div className="flex justify-between">
                                <span className={isDarkMode ? 'text-gray-400' : 'text-gray-600'}>Routine Type:</span>
                                <span className={isDarkMode ? 'text-gray-300' : 'text-gray-700'}>
                                  {routineInfo.routineType.charAt(0).toUpperCase() + routineInfo.routineType.slice(1)}
                                </span>
                              </div>
                              <div className="flex justify-between">
                                <span className={isDarkMode ? 'text-gray-400' : 'text-gray-600'}>Age Up:</span>
                                <span className={isDarkMode ? 'text-gray-300' : 'text-gray-700'}>
                                  {routineInfo.ageUp ? 'Yes' : 'No'}
                                </span>
                              </div>
                              <div className="flex justify-between">
                                <span className={isDarkMode ? 'text-gray-400' : 'text-gray-600'}>Extra Time:</span>
                                <span className={isDarkMode ? 'text-gray-300' : 'text-gray-700'}>
                                  {routineInfo.extraTime === '0' ? 'None' : `+${routineInfo.extraTime}s`}
                                </span>
                              </div>
                              <div className="flex justify-between">
                                <span className={isDarkMode ? 'text-gray-400' : 'text-gray-600'}>Type:</span>
                                <span className={isDarkMode ? 'text-gray-300' : 'text-gray-700'}>
                                  {routine.type || 'routine'}
                                </span>
                              </div>
                              <div className="flex justify-between">
                                <span className={isDarkMode ? 'text-gray-400' : 'text-gray-600'}>Stage:</span>
                                <span className={isDarkMode ? 'text-gray-300' : 'text-gray-700'}>
                                  {routine.stage?.name || `Stage ${routine.stageId}`}
                                </span>
                              </div>
                            </div>
                          </div>

                          {/* Studio & Performer Details */}
                          <div>
                            <h5 className={`font-medium mb-2 ${
                              isDarkMode ? 'text-white' : 'text-gray-900'
                            }`}>
                              Performer Details
                            </h5>
                            <div className="space-y-1 text-sm">
                              <div className="flex justify-between">
                                <span className={isDarkMode ? 'text-gray-400' : 'text-gray-600'}>Choreographer:</span>
                                <span className={isDarkMode ? 'text-gray-300' : 'text-gray-700'}>
                                  {routine.parentRoutine?.choreographer || 'Not specified'}
                                </span>
                              </div>
                              {routine.parentRoutine?.routineDancers?.length > 0 && (
                                <div className="flex justify-between">
                                  <span className={isDarkMode ? 'text-gray-400' : 'text-gray-600'}>Dancers:</span>
                                  <span className={isDarkMode ? 'text-gray-300' : 'text-gray-700'}>
                                    {routine.parentRoutine.routineDancers.length}
                                  </span>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>

                        {/* Dancer List (if available and reasonable number) */}
                        {routine.parentRoutine?.routineDancers && 
                         routine.parentRoutine.routineDancers.length > 1 && 
                         routine.parentRoutine.routineDancers.length <= 10 && (
                          <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-600">
                            <h5 className={`font-medium mb-2 ${
                              isDarkMode ? 'text-white' : 'text-gray-900'
                            }`}>
                              Dancers
                            </h5>
                            <div className="flex flex-wrap gap-2">
                              {routine.parentRoutine.routineDancers.map((dancer, idx) => (
                                <span 
                                  key={idx}
                                  className={`px-2 py-1 rounded-full text-xs ${
                                    isDarkMode 
                                      ? 'bg-gray-700 text-gray-300' 
                                      : 'bg-gray-100 text-gray-700'
                                  }`}
                                >
                                  {dancer.rosterDancers.firstName} {dancer.rosterDancers.lastName}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}

              {filteredAndSortedRoutines.length === 0 && (
                <div className="text-center py-12">
                  <Search className={`h-12 w-12 mx-auto mb-4 ${
                    isDarkMode ? 'text-gray-600' : 'text-gray-400'
                  }`} />
                  <p className={`text-lg ${
                    isDarkMode ? 'text-gray-400' : 'text-gray-600'
                  }`}>
                    No routines match your search
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EnhancedRoutineViewer;
